import { TImmiCardResponse } from '@verifime/components';
import { parseDateString, validationRules } from '@verifime/utils';
import { format, isValid } from 'date-fns';

const YYYY_MM_DD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The expiry as yyyy-MM-dd, the format the contract declares, or undefined when it is not a date, so that
 * `format` never throws. A yyyy-MM-dd string is passed through: new Date('2030-01-31') is midnight
 * UTC, which formats as the day before in a time zone behind UTC.
 */
function formatExpiry(expiry: unknown): string | undefined {
  if (typeof expiry === 'string' && YYYY_MM_DD.test(expiry)) {
    // Parsed as the form's own expiry rule parses it (parseDateString), so a string of the right
    // shape that is not a real date (2030-02-30, 2030-99-99) is left out, not sent.
    const date = parseDateString('', expiry) as Date;
    return isValid(date) ? format(date, 'yyyy-MM-dd') : undefined;
  }
  const date = new Date(expiry as string | Date);
  return isValid(date) ? format(date, 'yyyy-MM-dd') : undefined;
}

// The form holds the expiry as a Date, not the string the response type declares.
type TImmiCardFormData = Partial<Record<keyof TImmiCardResponse, unknown>>;

/**
 * Builds the `immiCard` object the wizard submits: the number upper-cased and without spaces, an
 * empty field (the given names, when the card prints none) left out, an expiry that is not a date
 * left out, the form data left as it was. It throws when there is no form data, so that a submit
 * never sends an empty document.
 */
export function toImmiCardPayload(immiCard: TImmiCardFormData): TImmiCardResponse {
  const payload: Record<string, unknown> = {};
  for (const [field, value] of Object.entries(immiCard)) {
    if (value !== undefined && value !== null && value !== '') {
      payload[field] = value;
    }
  }

  if (typeof payload.immiCardNumber === 'string') {
    payload.immiCardNumber = validationRules.normaliseImmiCardNumber(payload.immiCardNumber);
  }

  const expiry = formatExpiry(payload.immiCardExpiry);
  if (expiry) {
    payload.immiCardExpiry = expiry;
  } else {
    delete payload.immiCardExpiry;
  }
  return payload as TImmiCardResponse;
}
